/**
 * Migration Service
 * Migrates data from localStorage to SQLite database
 * Keeps localStorage backup for 7 days
 */

import sqliteService from './sqliteService';
import { getUserItem, setUserItem } from '../utils/userStorage';

const MIGRATION_KEY = 'migration_status';
const BACKUP_EXPIRY_DAYS = 7;

class MigrationService {
    constructor() {
        this.migrationStatus = this.loadMigrationStatus();
    }

    /**
     * Load migration status
     * @returns {Object} Migration status
     */
    loadMigrationStatus() {
        const status = getUserItem(MIGRATION_KEY);
        if (status) {
            return JSON.parse(status);
        }
        return {
            migrated: false,
            migrationDate: null,
            backupExpiryDate: null,
            errors: []
        };
    }

    /**
     * Save migration status
     */
    saveMigrationStatus() {
        setUserItem(MIGRATION_KEY, JSON.stringify(this.migrationStatus));
    }

    /**
     * Check if migration is needed
     * @returns {boolean} True if migration needed
     */
    isMigrationNeeded() {
        // Check if already migrated
        if (this.migrationStatus.migrated) {
            return false;
        }

        // Check if SQLite database exists
        const hasDatabase = localStorage.getItem('anitrakt_sqlite_db') !== null;
        if (hasDatabase) {
            // Database exists, mark as migrated
            this.migrationStatus.migrated = true;
            this.saveMigrationStatus();
            return false;
        }

        // Check if there's localStorage data to migrate
        const hasLocalStorageData = this.hasLocalStorageData();
        return hasLocalStorageData;
    }

    /**
     * Check if localStorage has data
     * @returns {boolean} True if data exists
     */
    hasLocalStorageData() {
        // Check for various localStorage keys used by the app
        const keysToCheck = [
            'anilist_cache_data',
            'anilist_groups_cache',
            'removed_anime',
            'anime_name_preference',
            'time_settings_hours_per_day'
        ];

        return keysToCheck.some(key => {
            const value = getUserItem(key);
            return value !== null && value !== undefined;
        });
    }

    /**
     * Create backup of all localStorage data
     * @returns {Object} Backup data or null if backup fails
     */
    createBackup() {
        try {
            const backup = {
                timestamp: Date.now(),
                data: {}
            };

            // Backup all localStorage items
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    backup.data[key] = localStorage.getItem(key);
                }
            }

            // Save backup
            const backupKey = `localStorage_backup_${backup.timestamp}`;
            localStorage.setItem(backupKey, JSON.stringify(backup));

            console.log(`💾 Created localStorage backup: ${backupKey}`);
            return backup;
        } catch (error) {
            // If quota exceeded or other error, log but don't fail
            if (error.name === 'QuotaExceededError') {
                console.warn('⚠️ localStorage quota exceeded - skipping backup creation');
                console.warn('   Migration will continue without backup');
            } else {
                console.warn('⚠️ Failed to create backup:', error.message);
            }
            return null;
        }
    }

    /**
     * Migrate data from localStorage to SQLite
     * @returns {Promise<Object>} Migration result
     */
    async migrate() {
        try {
            console.log('🔄 Starting migration from localStorage to SQLite...');

            // Try to create backup (optional - migration continues if this fails)
            const backup = this.createBackup();
            if (backup) {
                console.log('✅ Backup created successfully');
            } else {
                console.log('⏭️ Proceeding without backup');
            }

            const results = {
                userAnime: 0,
                groups: 0,
                settings: 0,
                errors: []
            };

            // Initialize SQLite database if not already done
            if (!sqliteService.isInitialized) {
                await sqliteService.initialize();
            }

            // 1. Migrate user anime data
            try {
                const cacheData = getUserItem('anilist_cache_data');
                if (cacheData) {
                    const parsedCache = JSON.parse(cacheData);
                    const animeData = JSON.parse(parsedCache.data || '{}');

                    if (animeData.MediaListCollection?.lists) {
                        animeData.MediaListCollection.lists.forEach(list => {
                            list.entries.forEach(entry => {
                                // Add user data (catalog now lives on server, no local upsert needed)
                                sqliteService.updateUserAnime(entry.media.id, {
                                    status: entry.status,
                                    progress: entry.progress,
                                    score: entry.score,
                                    notes: entry.notes,
                                    startedAt: entry.startedAt ? new Date(
                                        entry.startedAt.year,
                                        entry.startedAt.month - 1,
                                        entry.startedAt.day
                                    ).getTime() : null,
                                    completedAt: entry.completedAt ? new Date(
                                        entry.completedAt.year,
                                        entry.completedAt.month - 1,
                                        entry.completedAt.day
                                    ).getTime() : null
                                });

                                results.userAnime++;
                            });
                        });
                    }
                }
            } catch (error) {
                console.error('Error migrating user anime:', error);
                results.errors.push(`User anime: ${error.message}`);
            }

            // 2. Migrate removed anime
            try {
                const removedAnime = getUserItem('removed_anime');
                if (removedAnime) {
                    const removedIds = JSON.parse(removedAnime);
                    removedIds.forEach(animeId => {
                        sqliteService.updateUserAnime(animeId, {
                            isRemoved: true
                        });
                    });
                }
            } catch (error) {
                console.error('Error migrating removed anime:', error);
                results.errors.push(`Removed anime: ${error.message}`);
            }

            // 3. Save database
            sqliteService.saveDatabase();

            // 4. Mark migration as complete
            this.migrationStatus = {
                migrated: true,
                migrationDate: Date.now(),
                backupExpiryDate: Date.now() + (BACKUP_EXPIRY_DAYS * 24 * 60 * 60 * 1000),
                errors: results.errors,
                results
            };
            this.saveMigrationStatus();

            console.log('✅ Migration complete:', results);

            return {
                success: true,
                ...results
            };

        } catch (error) {
            console.error('❌ Migration failed:', error);
            this.migrationStatus.errors.push(error.message);
            this.saveMigrationStatus();

            throw error;
        }
    }

    /**
     * Restore from backup (rollback migration)
     * @param {number} backupTimestamp Backup timestamp
     */
    async restoreFromBackup(backupTimestamp) {
        try {
            const backupKey = `localStorage_backup_${backupTimestamp}`;
            const backupData = localStorage.getItem(backupKey);

            if (!backupData) {
                throw new Error('Backup not found');
            }

            const backup = JSON.parse(backupData);

            // Clear current localStorage (except backups)
            const keys = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && !key.startsWith('localStorage_backup_')) {
                    keys.push(key);
                }
            }
            keys.forEach(key => localStorage.removeItem(key));

            // Restore from backup
            Object.entries(backup.data).forEach(([key, value]) => {
                if (!key.startsWith('localStorage_backup_')) {
                    localStorage.setItem(key, value);
                }
            });

            // Reset migration status
            this.migrationStatus = {
                migrated: false,
                migrationDate: null,
                backupExpiryDate: null,
                errors: []
            };
            this.saveMigrationStatus();

            console.log('✅ Restored from backup');

        } catch (error) {
            console.error('❌ Restore failed:', error);
            throw error;
        }
    }

    /**
     * Clean up expired backups
     */
    cleanupExpiredBackups() {
        try {
            const now = Date.now();
            const backupsToDelete = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('localStorage_backup_')) {
                    try {
                        const backup = JSON.parse(localStorage.getItem(key));
                        const backupAge = now - backup.timestamp;
                        const maxAge = BACKUP_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

                        if (backupAge > maxAge) {
                            backupsToDelete.push(key);
                        }
                    } catch (error) {
                        // Invalid backup, delete it
                        backupsToDelete.push(key);
                    }
                }
            }

            backupsToDelete.forEach(key => {
                localStorage.removeItem(key);
                console.log(`🗑️ Deleted expired backup: ${key}`);
            });

            if (backupsToDelete.length > 0) {
                console.log(`✅ Cleaned up ${backupsToDelete.length} expired backups`);
            }

        } catch (error) {
            console.error('Error cleaning up backups:', error);
        }
    }

    /**
     * Get list of available backups
     * @returns {Array} List of backups with timestamps
     */
    getAvailableBackups() {
        const backups = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('localStorage_backup_')) {
                try {
                    const backup = JSON.parse(localStorage.getItem(key));
                    backups.push({
                        key,
                        timestamp: backup.timestamp,
                        date: new Date(backup.timestamp).toLocaleString(),
                        size: JSON.stringify(backup).length
                    });
                } catch (error) {
                    // Invalid backup, skip
                }
            }
        }

        return backups.sort((a, b) => b.timestamp - a.timestamp);
    }

    /**
     * Get migration status for UI
     * @returns {Object} Migration status
     */
    getStatus() {
        return {
            ...this.migrationStatus,
            needsMigration: this.isMigrationNeeded(),
            availableBackups: this.getAvailableBackups(),
            daysUntilBackupExpiry: this.migrationStatus.backupExpiryDate
                ? Math.ceil((this.migrationStatus.backupExpiryDate - Date.now()) / (24 * 60 * 60 * 1000))
                : null
        };
    }
}

// Export singleton instance
const migrationService = new MigrationService();
export default migrationService;
